import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { 
  Calendar, 
  Clock, 
  User, 
  Video,
  Phone,
  Building2,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  CalendarPlus,
  Mail,
  CalendarDays
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APPOINTMENT_TYPES = [
  { value: 'agency', label: 'RDV en agence', icon: Building2, color: 'bg-blue-500' },
  { value: 'call', label: 'Call conférence', icon: Video, color: 'bg-green-500' },
  { value: 'phone', label: 'Appel téléphonique', icon: Phone, color: 'bg-orange-500' },
];

const AppointmentRequestWidget = ({ theme, user, accountManager }) => {
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [myAppointments, setMyAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableManagers, setAvailableManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(accountManager || null);
  const [nextAvailabilities, setNextAvailabilities] = useState([]);
  const [loadingAvailabilities, setLoadingAvailabilities] = useState(false);

  // Helper to get avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    if (avatarPath.startsWith('/api/')) {
      return `${BACKEND_URL}${avatarPath}`;
    }
    return avatarPath;
  };

  // Form state
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('30');
  const [appointmentType, setAppointmentType] = useState('call');

  useEffect(() => {
    fetchMyAppointments();
    if (!accountManager) {
      fetchAvailableManagers();
    }
  }, [accountManager]);

  useEffect(() => {
    if (accountManager) {
      setSelectedManager(accountManager);
      fetchNextAvailabilities(accountManager.id);
    }
  }, [accountManager]);

  // Auto-load availabilities when managers are fetched and no account manager assigned
  useEffect(() => {
    if (!accountManager && availableManagers.length > 0 && !selectedManager) {
      // Auto-select first manager and load their availabilities
      const firstManager = availableManagers[0];
      setSelectedManager(firstManager);
      fetchNextAvailabilities(firstManager.id);
    }
  }, [accountManager, availableManagers, selectedManager]);

  // Fetch next available slots for the account manager
  const fetchNextAvailabilities = async (managerId) => {
    if (!managerId) return;
    setLoadingAvailabilities(true);
    try {
      // Get availability for next 7 days
      const slots = [];
      const today = new Date();
      
      for (let i = 1; i <= 7; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        
        try {
          const res = await api.get(`/calendar/availability/${managerId}`, {
            params: { date: dateStr }
          });
          if (res.data.available_slots && res.data.available_slots.length > 0) {
            slots.push({
              date: dateStr,
              dayName: date.toLocaleDateString('fr-FR', { weekday: 'long' }),
              dayNum: date.getDate(),
              month: date.toLocaleDateString('fr-FR', { month: 'short' }),
              slots: res.data.available_slots, // All slots available
              totalSlots: res.data.available_slots.length
            });
          }
          if (slots.length >= 5) break; // Show up to 5 days with availability
        } catch (e) {
          // Skip if error for this date
        }
      }
      setNextAvailabilities(slots);
    } catch (error) {
      console.error('Error fetching availabilities:', error);
    } finally {
      setLoadingAvailabilities(false);
    }
  };

  const fetchAvailableManagers = async () => {
    try {
      const res = await api.get('/calendar/available-managers');
      setAvailableManagers(res.data || []);
      // Auto-select first manager if only one
      if (res.data?.length === 1) {
        setSelectedManager(res.data[0]);
      }
    } catch (error) {
      console.error('Error fetching managers:', error);
    }
  };

  const fetchMyAppointments = async () => {
    try {
      const res = await api.get('/calendar/appointments');
      setMyAppointments(res.data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (date) => {
    if (!selectedManager?.id || !date) return;
    
    setLoadingSlots(true);
    try {
      const res = await api.get(`/calendar/availability/${selectedManager.id}`, {
        params: { date }
      });
      setAvailableSlots(res.data.available_slots || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
    if (selectedManager?.id) {
      fetchAvailability(date);
    }
  };

  const handleManagerChange = (managerId) => {
    const manager = availableManagers.find(m => m.id === managerId);
    setSelectedManager(manager);
    setSelectedTime('');
    setAvailableSlots([]);
    if (selectedDate) {
      fetchAvailability(selectedDate);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !subject || !selectedManager?.id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/calendar/appointments/request', {
        account_manager_id: selectedManager.id,
        requested_date: selectedDate,
        requested_time: selectedTime,
        duration_minutes: parseInt(duration),
        subject,
        description,
        appointment_type: appointmentType
      });

      toast.success('Demande de RDV envoyée');
      setShowRequestDialog(false);
      resetForm();
      fetchMyAppointments();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptProposal = async (appointmentId) => {
    try {
      await api.post(`/calendar/appointments/${appointmentId}/accept-proposal`);
      toast.success('Nouvelle date acceptée');
      fetchMyAppointments();
    } catch (error) {
      toast.error('Erreur lors de l\'acceptation');
    }
  };

  const resetForm = () => {
    setSelectedDate('');
    setSelectedTime('');
    setSubject('');
    setDescription('');
    setDuration('30');
    setAvailableSlots([]);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente', icon: Clock },
      accepted: { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmé', icon: CheckCircle },
      proposed_new_time: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Nouvelle date proposée', icon: Calendar },
      declined: { bg: 'bg-red-100', text: 'text-red-700', label: 'Décliné', icon: XCircle },
      cancelled: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Annulé', icon: XCircle }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={cn(style.bg, style.text, 'text-xs gap-1')}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  // Get tomorrow as min date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Card className="card">
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Calendar className="w-5 h-5" style={{ color: theme?.colors?.accent }} />
              Mes rendez-vous
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Demandez un RDV avec votre responsable
            </CardDescription>
          </div>
          <Button 
            onClick={() => setShowRequestDialog(true)}
            style={{ backgroundColor: theme?.colors?.accent }}
            className="w-full sm:w-auto"
          >
            <CalendarPlus className="w-4 h-4 mr-2" />
            Demander un RDV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Account Manager Card - Show for assigned manager OR selected manager */}
        {(accountManager || selectedManager) && (
          <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200">
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16 border-2 border-white shadow-lg">
                {(selectedManager || accountManager)?.avatar_url ? (
                  <AvatarImage 
                    src={getAvatarUrl((selectedManager || accountManager).avatar_url)} 
                    alt={`${(selectedManager || accountManager).first_name} ${(selectedManager || accountManager).last_name}`}
                  />
                ) : null}
                <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                  {(selectedManager || accountManager)?.first_name?.[0]}{(selectedManager || accountManager)?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-500">
                  {accountManager ? 'Votre responsable' : 'Responsable disponible'}
                </p>
                <h3 className="font-semibold text-lg text-slate-800">
                  {(selectedManager || accountManager)?.first_name} {(selectedManager || accountManager)?.last_name}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  {(selectedManager || accountManager)?.phone && (
                    <a 
                      href={`tel:${(selectedManager || accountManager).phone}`}
                      className="flex items-center gap-1 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {(selectedManager || accountManager).phone}
                    </a>
                  )}
                  {(selectedManager || accountManager)?.email && (
                    <a 
                      href={`mailto:${(selectedManager || accountManager).email}`}
                      className="flex items-center gap-1 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {(selectedManager || accountManager).email}
                    </a>
                  )}
                </div>
                
                {/* Manager selector if no assigned account manager */}
                {!accountManager && availableManagers.length > 1 && (
                  <div className="mt-2">
                    <Select
                      value={selectedManager?.id || ''}
                      onValueChange={(managerId) => {
                        const manager = availableManagers.find(m => m.id === managerId);
                        setSelectedManager(manager);
                        setNextAvailabilities([]);
                        if (manager) {
                          fetchNextAvailabilities(manager.id);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-[220px] h-8 text-xs">
                        <SelectValue placeholder="Changer de responsable" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableManagers.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.first_name} {m.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            
            {/* Next Availabilities */}
            {loadingAvailabilities ? (
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Chargement des disponibilités...
              </div>
            ) : nextAvailabilities.length > 0 ? (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                  <CalendarDays className="w-4 h-4" style={{ color: theme?.colors?.accent }} />
                  Prochaines disponibilités
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                  {nextAvailabilities.map((day, idx) => (
                    <button
                      key={day.date}
                      onClick={() => {
                        setSelectedDate(day.date);
                        setShowRequestDialog(true);
                        // Load all slots for this day
                        setAvailableSlots(day.slots);
                      }}
                      className="p-2 rounded-lg bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left group"
                    >
                      <div className="text-xs text-slate-500 capitalize">{day.dayName}</div>
                      <div className="font-semibold text-slate-800">{day.dayNum} {day.month}</div>
                      <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {day.totalSlots || day.slots.length} créneaux
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-4">
                Aucune disponibilité cette semaine. Demandez un RDV pour voir tous les créneaux.
              </p>
            )}
          </div>
        )}

        {/* Appointments List */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : myAppointments.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Aucun rendez-vous programmé</p>
            <Button 
              variant="link" 
              onClick={() => setShowRequestDialog(true)}
              className="mt-2"
              style={{ color: theme?.colors?.accent }}
            >
              Demander votre premier RDV
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {myAppointments.map(apt => (
              <div 
                key={apt.id}
                className="p-3 sm:p-4 rounded-lg border bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base truncate">{apt.subject}</p>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-xs sm:text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {apt.requested_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {apt.requested_time}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="truncate max-w-[100px] sm:max-w-none">{apt.account_manager_name}</span>
                      </span>
                    </div>
                    
                    {/* Show proposal if status is proposed_new_time */}
                    {apt.status === 'proposed_new_time' && (
                      <div className="mt-3 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs sm:text-sm text-blue-700 font-medium">Nouvelle date proposée :</p>
                        <p className="text-xs sm:text-sm text-blue-600">
                          {apt.proposed_date} à {apt.proposed_time}
                        </p>
                        {apt.manager_message && (
                          <p className="text-xs text-blue-500 mt-1">
                            &quot;{apt.manager_message}&quot;
                          </p>
                        )}
                        <Button 
                          size="sm" 
                          className="mt-2 w-full sm:w-auto"
                          onClick={() => handleAcceptProposal(apt.id)}
                          style={{ backgroundColor: theme?.colors?.accent }}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Accepter cette date
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="self-start">
                    {getStatusBadge(apt.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="sm:max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-lg">Demander un rendez-vous</DialogTitle>
            <DialogDescription className="text-sm">
              Choisissez une date et un créneau disponible
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 py-2 sm:py-4">
            {/* Account Manager Selection - show if no pre-assigned manager */}
            {!accountManager && availableManagers.length > 0 && (
              <div>
                <Label>Responsable *</Label>
                <Select 
                  value={selectedManager?.id || ''} 
                  onValueChange={handleManagerChange}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choisir un responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableManagers.map(manager => (
                      <SelectItem key={manager.id} value={manager.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {manager.first_name} {manager.last_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Account Manager Info - show if assigned or selected */}
            {selectedManager && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0"
                  style={{ backgroundColor: theme?.colors?.accent }}
                >
                  {selectedManager.first_name?.[0]}{selectedManager.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{selectedManager.first_name} {selectedManager.last_name}</p>
                  <p className="text-xs text-slate-500">
                    {accountManager ? 'Votre responsable de compte' : 'Responsable sélectionné'}
                  </p>
                </div>
              </div>
            )}

            {/* No managers available */}
            {!accountManager && availableManagers.length === 0 && !loading && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-yellow-700">
                  Aucun responsable disponible pour le moment.
                </p>
              </div>
            )}

            {/* Date Selection */}
            {selectedManager && (
              <div>
                <Label>Date souhaitée *</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  min={minDate}
                  className="mt-1"
                />
              </div>
            )}

            {/* Time Selection */}
            {selectedDate && selectedManager && (
              <div>
                <Label>Créneau horaire *</Label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 mt-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Chargement des disponibilités...</span>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-700">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      Aucun créneau disponible ce jour. Essayez une autre date.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {availableSlots.map((slot, idx) => (
                      <Button
                        key={idx}
                        variant={selectedTime === slot.start ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setSelectedTime(slot.start)}
                        className={cn(
                          "text-xs",
                          selectedTime === slot.start && "ring-2 ring-offset-2"
                        )}
                        style={selectedTime === slot.start ? { backgroundColor: theme?.colors?.accent } : {}}
                      >
                        {slot.start}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Duration */}
            {selectedManager && (
              <div>
                <Label>Durée</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 heure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Appointment Type */}
            {selectedManager && (
              <div>
                <Label>Type de rendez-vous</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {APPOINTMENT_TYPES.map(type => {
                    const Icon = type.icon;
                    const isSelected = appointmentType === type.value;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setAppointmentType(type.value)}
                        className={cn(
                          "flex flex-col items-center gap-1 sm:gap-1.5 p-2 sm:p-3 rounded-lg border-2 transition-all",
                          isSelected 
                            ? "border-slate-900 bg-slate-50" 
                            : "border-slate-200 hover:border-slate-300"
                        )}
                      >
                        <div className={cn("w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white", type.color)}>
                          <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-center leading-tight">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subject */}
            {selectedManager && (
              <div>
                <Label>Sujet du RDV *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ex: Point sur le projet, Questions facturation..."
                  className="mt-1"
                />
              </div>
            )}

            {/* Description */}
            {selectedManager && (
              <div>
                <Label>Description (optionnel)</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Précisez les sujets que vous souhaitez aborder..."
                  rows={3}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-4 mt-2 border-t sticky bottom-0 bg-white pb-1">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !selectedDate || !selectedTime || !subject || !selectedManager}
              style={{ backgroundColor: theme?.colors?.accent }}
              className="w-full sm:w-auto"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AppointmentRequestWidget;
